// Sprint-V25 Issue L (Sa 12.09.2026) — StorySection · Russell-Brunson-Reveal
// Layout:
//   1. Section-Kicker („Warum es sich wiederholt") — kleiner Uppercase-Trigger
//   2. Reveal-Copy in grosszuegigen Zeilen (Steve-Jobs-Weissraum)
//   3. Vier Tier-Cards horizontal Row (Icon + Name + Motto) — der USP
//   4. Zusammenfuehrender CTA
// Away-from-Pain: „Es wiederholt sich" → „Du bist nicht der Grund"

import React from "react";
import { FOKUSTYPEN_GRID, CTA_LABEL } from "@/data/landingHookCopy";
import iconHund from "@/assets/tier-icons/hund.png";
import iconKatze from "@/assets/tier-icons/katze.png";
import iconBiene from "@/assets/tier-icons/biene.png";
import iconEule from "@/assets/tier-icons/eule.png";

// Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaeter) — Andreas-Direktive:
// Marken-Icons (Hund/Katze/Biene/Eule) statt Lucide.
const TIER_ICON_SRC = { hund: iconHund, katze: iconKatze, biene: iconBiene, eule: iconEule };
const TIER_BG = {
  hund: "from-orange-100 to-orange-50 dark:from-orange-900/30 dark:to-orange-950/40 text-orange-700 dark:text-orange-300",
  katze: "from-slate-200 to-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-700 dark:text-slate-200",
  biene: "from-amber-100 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-950/40 text-amber-700 dark:text-amber-300",
  eule: "from-indigo-100 to-blue-50 dark:from-indigo-900/30 dark:to-blue-950/40 text-indigo-700 dark:text-indigo-300",
};

export default function StorySection({ onCtaClick }) {
  return (
    <section
      data-testid="lp-story-section"
      className="w-full bg-white dark:bg-slate-950 py-16 sm:py-28"
    >
      <div className="mx-auto w-full max-w-md sm:max-w-5xl px-6 sm:px-10 flex flex-col items-center text-center">
        {/* 1. Kicker */}
        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-600 dark:text-slate-300">
          Warum es sich wiederholt
        </span>

        {/* 2. Reveal-Copy */}
        <div
          data-testid="lp-story-copy"
          className="mt-8 sm:mt-12 text-[20px] sm:text-[32px] leading-[1.4] font-medium text-slate-800 dark:text-slate-100 max-w-[28ch] sm:max-w-3xl space-y-6 sm:space-y-8"
        >
          <p>
            Es wiederholt sich.
            <br />
            Immer wieder.
          </p>
          <p>
            Du meinst es gut.
            <br />
            Und trotzdem entsteht Distanz.
          </p>
          <p className="text-slate-500 dark:text-slate-400 text-[17px] sm:text-[24px]">
            Du bist nicht der Grund.
            <br />
            Der Grund ist ein Muster.
          </p>
        </div>

        {/* 3. Vier Tier-Cards — 2×2-Kachel-Anordnung immer (Andreas-Direktive
            Sa 12.09.2026): Katze oben-links, Eule oben-rechts, Hund unten-links,
            Biene unten-rechts. Reihenfolge kommt aus FOKUSTYPEN_GRID. */}
        <div
          data-testid="lp-story-tiers"
          className="mt-12 sm:mt-20 grid grid-cols-2 gap-3 sm:gap-6 w-full max-w-md sm:max-w-3xl"
        >
          {FOKUSTYPEN_GRID.map((t) => {
            const iconSrc = TIER_ICON_SRC[t.key];
            const grad = TIER_BG[t.key];
            return (
              <div
                key={t.key}
                data-testid={`lp-story-tier-${t.key}`}
                className={`rounded-2xl bg-gradient-to-br ${grad} p-5 sm:p-6 flex flex-col items-center gap-3 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}
              >
                <img src={iconSrc} alt={t.label} loading="lazy" className="w-14 h-14 sm:w-16 sm:h-16 object-contain" />
                <div>
                  <div className="text-base sm:text-lg font-extrabold">{t.label}</div>
                  <div className="text-[12px] sm:text-sm opacity-80 leading-snug mt-0.5">
                    {t.motto}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 4. Reveal-Pointer */}
        <p className="mt-12 text-[18px] sm:text-[22px] leading-relaxed font-semibold text-slate-900 dark:text-slate-100 max-w-[28ch]">
          In fünf Minuten weißt du,
          <br />
          welches Muster deins ist.
        </p>

        {/* 5. CTA */}
        <button
          type="button"
          data-testid="lp-story-cta"
          onClick={() => onCtaClick && onCtaClick("story")}
          className="mt-8 w-full sm:w-auto min-h-[56px] px-8 sm:px-10 rounded-2xl font-bold text-[18px] text-white bg-[var(--fts-accent,#DC2626)] hover:bg-[var(--fts-accent-hover,#B91C1C)] shadow-[0_10px_30px_-8px_rgba(220,38,38,0.5)] hover:-translate-y-0.5 transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-red-200 dark:focus-visible:ring-red-900/40"
        >
          {CTA_LABEL}
        </button>
      </div>
    </section>
  );
}
